import type { TutorialStep } from '@/config/objects/release-notes';
import { toast } from '@startupquickstart/frontend';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/** How long to wait for the first step's element to appear before giving up. */
const SELECTOR_POLL_TIMEOUT_MS = 3000;
/** Interval between selector existence checks. */
const SELECTOR_POLL_INTERVAL_MS = 250;

/**
 * Poll the DOM for `selector`, resolving to the element once it exists or
 * `null` if it never appears within `timeoutMs`. Used to give a freshly
 * navigated page time to render its highlight targets before the tour starts.
 */
function waitForSelector(
  selector: string,
  timeoutMs: number,
  intervalMs: number,
): Promise<Element | null> {
  return new Promise((resolve) => {
    const existing = document.querySelector(selector);
    if (existing) {
      resolve(existing);
      return;
    }
    const started = Date.now();
    const timer = window.setInterval(() => {
      const el = document.querySelector(selector);
      if (el) {
        window.clearInterval(timer);
        resolve(el);
      } else if (Date.now() - started >= timeoutMs) {
        window.clearInterval(timer);
        resolve(null);
      }
    }, intervalMs);
  });
}

/**
 * Returns `runFeatureTour`, which launches a driver.js guided tour for the
 * given steps.
 *
 * driver.js and its stylesheet are imported lazily so the ~30KB library only
 * loads when a user actually starts a tour. If the first step declares a
 * `route` different from the current one we navigate there first, then poll
 * for its element to render. We only build tour steps from selectors that
 * currently match the DOM — so a partially-stale doc still highlights whatever
 * it can. If nothing matches we surface a toast rather than an empty tour.
 *
 * Multi-page tours are out of scope for v1: every driven step must resolve on
 * the page we end up on. The per-step `route` field is reserved for a future
 * version that can advance across navigations.
 */
export function useFeatureTour(): {
  runFeatureTour: (steps: TutorialStep[]) => Promise<void>;
} {
  const navigate = useNavigate();
  const location = useLocation();

  const runFeatureTour = useCallback(
    async (steps: TutorialStep[]): Promise<void> => {
      const firstStep = steps[0];
      if (!firstStep) return;

      const [{ driver }] = await Promise.all([
        import('driver.js'),
        import('driver.js/dist/driver.css'),
      ]);

      // Navigate to the first step's page if it lives elsewhere.
      if (firstStep.route && firstStep.route !== location.pathname) {
        navigate(firstStep.route);
      }

      // Give the (possibly just-navigated) page a chance to render.
      await waitForSelector(
        firstStep.selector,
        SELECTOR_POLL_TIMEOUT_MS,
        SELECTOR_POLL_INTERVAL_MS,
      );

      // Build driver steps only from selectors that resolve right now.
      const driverSteps = steps
        .filter((step) => document.querySelector(step.selector) !== null)
        .map((step) => ({
          element: step.selector,
          popover: {
            title: step.title,
            description: step.description ?? '',
          },
        }));

      if (driverSteps.length === 0) {
        toast.error('Could not find the highlighted elements on this page');
        return;
      }

      const driverObj = driver({ showProgress: true, steps: driverSteps });
      driverObj.drive();
    },
    [navigate, location.pathname],
  );

  return { runFeatureTour };
}
